import React, { useState, useRef, useEffect } from 'react';
import { 
  BellIcon, 
  CheckIcon, 
  XMarkIcon, 
  TrashIcon,
  Cog6ToothIcon,
  FireIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  UserIcon,
  BriefcaseIcon,
  CalendarIcon,
  ComputerDesktopIcon,
  QueueListIcon,
  EyeSlashIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';
import { BellIcon as BellIconSolid } from '@heroicons/react/24/solid';
import { useNotifications } from '../../hooks/useNotifications';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { Notification } from '../../types/notification';
import { format } from 'date-fns';
import { hu } from 'date-fns/locale';
import { useNotificationCenter } from '../../contexts/NotificationContext';

const NotificationCenter: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { isOpen, setIsOpen } = useNotificationCenter();
  const [activeTab, setActiveTab] = useState<'all' | 'unread'>('all');
  const [notificationToDelete, setNotificationToDelete] = useState<Notification | null>(null);
  const [showClearAllModal, setShowClearAllModal] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  
  const {
    notifications,
    loading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications,
    getStats,
    getUnreadNotifications,
    getRecentNotifications,
  } = useNotifications();

  const stats = getStats();

  // Kattintás kívülre - sidebar bezárása
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Escape key listener
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // Értesítés típus alapján ikon
  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'task_assigned':
        return <UserIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />;
      case 'task_completed':
        return <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400" />;
      case 'task_overdue':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-400" />;
      case 'project_update':
        return <BriefcaseIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />;
      case 'deadline_reminder':
        return <ClockIcon className="h-5 w-5 text-orange-600 dark:text-orange-400" />;
      case 'system':
        return <ComputerDesktopIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />;
      case 'mention':
        return <InformationCircleIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />;
      default:
        return <BellIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />;
    }
  };

  // Prioritás alapján háttérszín
  const getPriorityColor = (priority: Notification['priority']) => {
    switch (priority) {
      case 'urgent':
        return 'border-l-red-500 bg-red-50 dark:bg-red-900/20';
      case 'high':
        return 'border-l-orange-500 bg-orange-50 dark:bg-orange-900/20';
      case 'medium':
        return 'border-l-yellow-500 bg-yellow-50 dark:bg-yellow-900/20';
      case 'low':
        return 'border-l-green-500 bg-green-50 dark:bg-green-900/20';
      default:
        return 'border-l-gray-500 bg-gray-50 dark:bg-gray-700';
    }
  };

  // Értesítés kezelése
  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.isRead) {
      await markAsRead(notification.id);
    }

    if (notification.actionUrl) {
      navigate(notification.actionUrl);
      setIsOpen(false);
    }
  };

  // Összes megjelölése olvasottként
  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  // Összes törlése
  const handleClearAll = () => {
    setShowClearAllModal(true);
  };

  // Összes törlése megerősítése
  const confirmClearAll = async () => {
    await clearAllNotifications();
    setShowClearAllModal(false);
  };

  // Értesítés törlése
  const handleDeleteNotification = (notification: Notification, e: React.MouseEvent) => {
    e.stopPropagation();
    setNotificationToDelete(notification);
  };

  // Értesítés törlése megerősítése
  const confirmDeleteNotification = async () => {
    if (notificationToDelete) {
      await deleteNotification(notificationToDelete.id);
      setNotificationToDelete(null);
    }
  };

  // Szűrt értesítések
  const filteredNotifications = activeTab === 'unread' 
    ? getUnreadNotifications() 
    : getRecentNotifications(20);

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[90] transition-opacity duration-300"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Notification Sidebar - csak akkor rendereljük, ha nyitva van */}
      {isOpen && (
        <div
          ref={sidebarRef}
          className="fixed top-0 right-0 h-screen w-96 bg-white dark:bg-gray-800 shadow-2xl transform translate-x-0 transition-transform duration-300 ease-in-out z-[100] flex flex-col"
        >
         {/* Header */}
         <div className="px-6 py-4 border-b border-indigo-200/60 dark:border-indigo-700/50 bg-gradient-to-r from-indigo-100/50 via-purple-100/30 to-blue-100/50 dark:from-indigo-900/20 dark:via-purple-900/15 dark:to-blue-900/20">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-indigo-800 dark:text-indigo-200">
              🔔 {t('notifications.title')}
            </h2>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 text-indigo-600 dark:text-indigo-400 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-indigo-100/60 dark:hover:bg-indigo-900/30 rounded-lg transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          
           {/* Stats */}
           <div className="mt-3 text-sm text-indigo-600 dark:text-indigo-400">
             {stats.unread > 0 ? (
               <span className="font-medium text-indigo-700 dark:text-indigo-300">
                 ✨ {stats.unread} {t('notifications.unread').toLowerCase()}
               </span>
             ) : (
               <span>✅ {t('notifications.noUnread')}</span>
             )}
           </div>
        </div>

        {/* Action Buttons */}
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <div className="flex items-center justify-between">
            <div className="flex space-x-1">
               <button
                 onClick={() => setActiveTab('all')}
                 className={`p-2 rounded-lg transition-colors relative ${
                   activeTab === 'all' 
                     ? 'bg-indigo-100 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300' 
                     : 'text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/10'
                 }`}
                title={`${t('notifications.all')} (${stats.total})`}
              >
                <QueueListIcon className="h-5 w-5" />
                {stats.total > 0 && (
                  <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-indigo-500 rounded-full min-w-[16px] h-[16px]">
                    {stats.total > 99 ? '99+' : stats.total}
                  </span>
                )}
              </button>
               <button
                 onClick={() => setActiveTab('unread')}
                 className={`p-2 rounded-lg transition-colors relative ${
                   activeTab === 'unread' 
                     ? 'bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300' 
                     : 'text-gray-500 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/10'
                 }`}
                title={`${t('notifications.unread')} (${stats.unread})`}
              >
                <EyeSlashIcon className="h-5 w-5" />
                {stats.unread > 0 && (
                  <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-purple-500 rounded-full min-w-[16px] h-[16px]">
                    {stats.unread > 99 ? '99+' : stats.unread}
                  </span>
                )}
              </button>
            </div>
            
            <div className="flex items-center space-x-1">
               <button
                 onClick={handleMarkAllAsRead}
                 className="p-2 text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors disabled:opacity-50"
                 disabled={stats.unread === 0}
                 title={t('notifications.markAllRead')}
               >
                <CheckIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => navigate('/settings/notifications')}
                className="p-2 text-indigo-500 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors"
                title="Beállítások"
              >
                <Cog6ToothIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Notifications List */}
        <div className="flex-1 overflow-y-auto scrollbar-thin min-h-0">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="text-center py-8 px-4">
              <BellIcon className="h-12 w-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
              <p className="text-gray-500 dark:text-gray-400 text-base font-medium mb-1">
                {activeTab === 'unread' 
                  ? t('notifications.noUnread') 
                  : t('notifications.noNotifications')
                }
              </p>
              <p className="text-gray-400 dark:text-gray-500 text-sm">
                {activeTab === 'unread' 
                  ? 'Minden értesítés el lett olvasva' 
                  : 'Még nincsenek értesítések'
                }
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {filteredNotifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`relative px-4 py-3 border-l-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors group ${
                    notification.isRead ? 'bg-white dark:bg-gray-800' : getPriorityColor(notification.priority)
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-0.5">
                      {getNotificationIcon(notification.type)}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <p className={`text-sm font-medium leading-tight ${
                          notification.isRead ? 'text-gray-700 dark:text-gray-300' : 'text-gray-900 dark:text-white'
                        }`}>
                          {notification.title}
                        </p>
                        <button
                          onClick={(e) => handleDeleteNotification(notification, e)}
                          className="ml-2 p-1 text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                      
                      <p className={`text-sm mt-1 leading-tight ${
                        notification.isRead ? 'text-gray-500 dark:text-gray-400' : 'text-gray-600 dark:text-gray-300'
                      }`}>
                        {notification.message}
                      </p>
                      
                      <div className="flex items-center justify-between mt-1.5">
                        <p className="text-xs text-gray-400 dark:text-gray-500">
                          {format(notification.createdAt, 'MM. dd. HH:mm', { locale: hu })}
                        </p>
                        
                        {notification.createdByName && (
                          <p className="text-xs text-gray-400 dark:text-gray-500 truncate ml-2">
                            {notification.createdByName}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {!notification.isRead && (
                      <div className="flex-shrink-0 mt-1">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

         {/* Footer */}
         {filteredNotifications.length > 0 && (
           <div className="px-4 py-3 border-t border-indigo-200/60 dark:border-indigo-700/50 bg-gradient-to-r from-indigo-50/50 via-purple-50/30 to-blue-50/50 dark:from-indigo-900/10 dark:via-purple-900/5 dark:to-blue-900/10">
            <div className="flex justify-between items-center">
              <button
                onClick={handleClearAll}
                className="p-2 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                title={t('notifications.clearAll')}
              >
                <TrashIcon className="h-5 w-5" />
              </button>
               <button
                 onClick={() => {
                   navigate('/notifications');
                   setIsOpen(false);
                 }}
                 className="p-2 text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors"
                 title={t('notifications.viewAll')}
               >
                <ArrowTopRightOnSquareIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}
        </div>
      )}

      {/* Egyedi értesítés törlése modal */}
      {notificationToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full shadow-2xl">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-red-600 to-red-700 px-4 py-3 rounded-t-xl">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                  <XMarkIcon className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white">
                  Értesítés törlése
                </h3>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex-shrink-0">
                  {getNotificationIcon(notificationToDelete.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-gray-900 dark:text-gray-100 truncate">
                    {notificationToDelete.title}
                  </h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                    {notificationToDelete.message}
                  </p>
                </div>
              </div>
              
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
                <p className="text-sm text-red-800 dark:text-red-200">
                  <strong>Figyelem!</strong> Ez a művelet véglegesen törli az értesítést.
                </p>
              </div>
              
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Biztosan törölni szeretnéd ezt az értesítést?
              </p>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 rounded-b-xl border-t border-gray-200 dark:border-gray-600">
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setNotificationToDelete(null)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-500 transition-colors text-sm"
                >
                  Mégse
                </button>
                <button
                  onClick={confirmDeleteNotification}
                  className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-800 transition-all shadow-lg text-sm font-medium"
                >
                  🗑️ Törlés
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Összes értesítés törlése modal */}
      {showClearAllModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full shadow-2xl">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-red-600 to-red-700 px-4 py-3 rounded-t-xl">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                  <TrashIcon className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white">
                  Összes értesítés törlése
                </h3>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <div className="flex items-center justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
                  <TrashIcon className="w-8 h-8 text-red-600 dark:text-red-400" />
                </div>
              </div>
              
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
                <p className="text-sm text-red-800 dark:text-red-200">
                  <strong>Figyelem!</strong> Ez a művelet véglegesen törli az összes értesítést.
                </p>
                <p className="text-sm text-red-700 dark:text-red-300 mt-2">
                  Összesen <strong>{stats.total}</strong> értesítés lesz törölve.
                </p>
              </div>
              
              <p className="text-sm text-gray-600 dark:text-gray-300 text-center">
                Biztosan törölni szeretnéd az összes értesítést? Ez a művelet nem vonható vissza.
              </p>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 rounded-b-xl border-t border-gray-200 dark:border-gray-600">
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowClearAllModal(false)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-500 transition-colors text-sm"
                >
                  Mégse
                </button>
                <button
                  onClick={confirmClearAll}
                  className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-800 transition-all shadow-lg text-sm font-medium"
                >
                  🗑️ Összes törlése
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NotificationCenter; 